import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  output,
  OutputEmitterRef,
  Signal
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { combineLatest, map, Observable, switchMap, take } from 'rxjs';
import {
  GEOLOCATION_TOKEN,
  GeolocationProvider,
  GlobalRefreshService,
  LocationAccessService,
  LocationStorage,
  SelectedLocation
} from '@core';

@Component({
  selector: 'lib-location-info-box',
  templateUrl: './location-info-box.component.html',
  styleUrls: ['./location-info-box.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true
})
export class LocationInfoBoxComponent {
  private readonly geolocationProvider: GeolocationProvider = inject(GEOLOCATION_TOKEN);
  private readonly locationAccessService: LocationAccessService = inject(LocationAccessService);
  private readonly locationStorage: LocationStorage = inject(LocationStorage);
  private readonly globalRefreshService: GlobalRefreshService = inject(GlobalRefreshService);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);

  readonly clicked: OutputEmitterRef<void> = output<void>();

  readonly locationAcquired: OutputEmitterRef<SelectedLocation> = output<SelectedLocation>();

  private readonly hasLocation$: Observable<boolean> = this.locationStorage.location$.pipe(
    map((location: SelectedLocation | null) => !!location)
  );

  private readonly title$: Observable<string> = this.globalRefreshService.refresh$.pipe(
    switchMap(() =>
      combineLatest([
        this.geolocationProvider.isLocationServicesEnabled(),
        this.geolocationProvider.isPermissionGranted()
      ])
    ),
    map(([isEnabled, isGranted]: [boolean, boolean]) =>
      isEnabled && !isGranted ? 'Nadaj uprawnienia do lokalizacji' : 'Włącz lokalizację'
    )
  );

  readonly isVisible: Signal<boolean> = toSignal(this.hasLocation$.pipe(map((hasLocation: boolean) => !hasLocation)), {
    initialValue: false
  });

  readonly title: Signal<string> = toSignal(this.title$, { initialValue: 'Włącz lokalizację' });

  readonly subtitle: string = 'Dzięki lokalizacji łatwiej znajdziesz gierki';

  openLocationFlow(): void {
    this.clicked.emit();

    this.locationAccessService
      .ensureLocation$()
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe((location: SelectedLocation | null) => {
        if (location) this.locationAcquired.emit(location);
      });
  }
}
